import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { WaveIcon } from '../icons/SvgIcons';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Chip,
  Grid,
  Stack,
  Avatar,
  Alert,
  CircularProgress,
  Divider,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Skeleton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
} from '@mui/material';
import {
  AccessTime,
  History,
  VideoCall,
  ExitToApp,
  Refresh,
  CheckCircle,
  People,
  Timer,
  PhoneInTalk,
  Search,
  PlayCircleOutline,
  ArticleOutlined,
  SummarizeOutlined,
  FiberManualRecord,
  Star,
  SmartToy,
  NotificationsActive,
  Delete,
  Close,
  ExpandMore,
  ExpandLess,
  SummarizeOutlined as SummaryIcon,
  Description,
} from '@mui/icons-material';
import { useAuthStore } from '../../stores/authStore';
import { useCallRedirection } from '../../hooks/useWebSocket';
import { apiRequest } from '../../utils/api';
import { MobileBottomNav } from '../common/MobileBottomNav';

function PatientDashboard() {
  const { user, handleAuthError, logout } = useAuthStore();
  const navigate = useNavigate();
  const [isInQueue, setIsInQueue] = useState(false);
  const [queuePosition, setQueuePosition] = useState(null);
  const [estimatedWaitTime, setEstimatedWaitTime] = useState(null);
  const [loadingQueue, setLoadingQueue] = useState(false);

  const [callHistory, setCallHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedTab, setSelectedTab] = useState(0);

  const { isConnected: wsConnected } = useCallRedirection();

  // AI Direct call
  const [startingAICall, setStartingAICall] = useState(false);

  // Reminders
  const [reminders, setReminders] = useState([]);
  const [loadingReminders, setLoadingReminders] = useState(false);



  // Call summary dialog
  const [summaryDialog, setSummaryDialog] = useState({ open: false, callId: null, summary: '', loading: false });

  // Expandable call detail (replaces video dialog)
  const [expandedCallId, setExpandedCallId] = useState(null);
  const [expandedNotes, setExpandedNotes] = useState({});
  const [expandedVideoUrl, setExpandedVideoUrl] = useState({});
  const [recordingLoading, setRecordingLoading] = useState(null);

  const fetchCallSummary = async (callId) => {
    setSummaryDialog({ open: true, callId, summary: '', loading: true });
    try {
      const response = await apiRequest(`/calls/${callId}/notes`);
      const notes = response.notes || [];
      const summaryNote = notes.find(n => n.note_type === 'ai_summary');
      const transcriptNote = notes.find(n => n.note_type === 'ai_transcript');
      const content = summaryNote?.content || transcriptNote?.content || 'No hay resumen disponible para esta llamada.';
      setSummaryDialog({ open: true, callId, summary: content, loading: false });
    } catch (error) {
      console.error('Error fetching call summary:', error);
      setSummaryDialog({ open: true, callId, summary: 'Error al cargar el resumen.', loading: false });
    }
  };

  const playRecording = async (callId) => {
    if (expandedVideoUrl[callId]) return;
    setRecordingLoading(callId);
    try {
      const response = await apiRequest(`/calls/${callId}/recordings`);
      const recordings = response.recordings || [];
      if (recordings.length > 0) {
        const urlResponse = await apiRequest(`/calls/${callId}/recordings/${recordings[0].id}/url`);
        if (urlResponse.url) {
          setExpandedVideoUrl(prev => ({ ...prev, [callId]: urlResponse.url }));
          return;
        }
      }
      setExpandedVideoUrl(prev => ({ ...prev, [callId]: 'none' }));
    } catch (error) {
      console.error('Error fetching recording URL:', error);
      setExpandedVideoUrl(prev => ({ ...prev, [callId]: 'none' }));
    } finally {
      setRecordingLoading(null);
    }
  };

  const toggleCallExpand = async (callId) => {
    if (expandedCallId === callId) {
      setExpandedCallId(null);
      return;
    }
    setExpandedCallId(callId);
    if (!expandedNotes[callId]) {
      setExpandedNotes(prev => ({ ...prev, [callId]: { loading: true, notes: [] } }));
      try {
        const data = await apiRequest(`/calls/${callId}/notes`);
        setExpandedNotes(prev => ({ ...prev, [callId]: { loading: false, notes: data.notes || [] } }));
      } catch (e) {
        setExpandedNotes(prev => ({ ...prev, [callId]: { loading: false, notes: [] } }));
      }
    }
  };

  const getNoteContent = (notes, type) => {
    if (type === 'summary') {
      const n = notes.find(n => n.note_type === 'ai_generated_summary') || notes.find(n => n.note_type === 'ai_summary');
      return n?.content || null;
    }
    return (notes.find(n => n.note_type === 'ai_transcript'))?.content || null;
  };

  const startAICall = async () => {
    try {
      setStartingAICall(true);
      const response = await apiRequest('/calls/ai-direct', { method: 'POST' });
      navigate(`/video-call/${response.call_id}`, {
        state: {
          token: response.token,
          serverUrl: response.server_url,
          roomName: response.room_id,
          mode: 'ai_direct',
        },
      });
    } catch (error) {
      console.error('Error starting AI call:', error);
      if (error?.response?.status === 401) handleAuthError(error);
    } finally {
      setStartingAICall(false);
    }
  };

  const fetchReminders = async () => {
    try {
      setLoadingReminders(true);
      const data = await apiRequest('/reminders/');
      setReminders(data.reminders || []);
    } catch (error) {
      setReminders([]);
    } finally {
      setLoadingReminders(false);
    }
  };

  const deleteReminder = async (id) => {
    try {
      await apiRequest(`/reminders/${id}`, { method: 'DELETE' });
      setReminders(prev => prev.filter(r => r.id !== id));
    } catch (error) {
      console.error('Error deleting reminder:', error);
    }
  };



  const joinQueue = async () => {
    try {
      setLoadingQueue(true);
      const response = await apiRequest('/queue/join', {
        method: 'POST',
        body: JSON.stringify({
          reason: 'Solicitud de acompañamiento',
          priority: 3,
          notes: ''
        })
      });
      setIsInQueue(true);
      setQueuePosition(response.position);
      setEstimatedWaitTime(response.estimated_wait);
    } catch (error) {
      console.error('Error joining queue:', error);
      if (error?.response?.status === 401) handleAuthError(error);
    } finally {
      setLoadingQueue(false);
    }
  };

  const leaveQueue = async () => {
    try {
      setLoadingQueue(true);
      await apiRequest('/queue/leave', {
        method: 'POST',
        body: JSON.stringify({ patientId: user.id })
      });
      setIsInQueue(false);
      setQueuePosition(null);
      setEstimatedWaitTime(null);
    } catch (error) {
      console.error('Error leaving queue:', error);
      if (error?.response?.status === 401) handleAuthError(error);
    } finally {
      setLoadingQueue(false);
    }
  };

  const checkQueueStatus = async () => {
    try {
      const response = await apiRequest(`/queue/status?patientId=${user.id}`);
      if (response.inQueue) {
        setIsInQueue(true);
        setQueuePosition(response.position);
        setEstimatedWaitTime(response.estimatedWaitTime);
      } else {
        setIsInQueue(false);
        setQueuePosition(null);
        setEstimatedWaitTime(null);
      }
    } catch (error) {
      console.error('Error checking queue status:', error);
      if (error?.response?.status === 401) handleAuthError(error);
    }
  };

  const fetchCallHistory = async () => {
    try {
      setLoadingHistory(true);
      if (!user?.id) {
        setCallHistory([]);
        return;
      }
      const history = await apiRequest(`/calls/history?userId=${user.id}`);
      const calls = (history.calls || []).map(call => ({
        ...call,
        patientName: call.patient
          ? `${call.patient.first_name || ''} ${call.patient.last_name || ''}`.trim()
          : call.patientName || 'Paciente',
        companionName: call.employee
          ? `${call.employee.first_name || ''} ${call.employee.last_name || ''}`.trim()
          : call.employeeName || 'IA Vincula',
        startedAt: call.started_at || call.startedAt,
        duration: call.duration || call.duration_seconds,
      }));
      setCallHistory(calls);
    } catch (error) {
      console.error('Error fetching call history:', error);
      if (error?.response?.status === 401) handleAuthError(error);
      setCallHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  useEffect(() => {
    if (user?.id) {
      checkQueueStatus();
      fetchCallHistory();
      fetchReminders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const filteredCallHistory = callHistory.filter((call) => {
    const matchesSearch = searchTerm === '' ||
      call.companionName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      formatDate(call.started_at || call.created_at).toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || call.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': case 'ended': return 'success';
      case 'cancelled': return 'error';
      case 'active': case 'in_progress': return 'warning';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': case 'ended': return 'Completada';
      case 'cancelled': return 'Cancelada';
      case 'active': case 'in_progress': return 'En curso';
      default: return status;
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>
      {/* Top Navigation */}
      <Box
        component="header"
        sx={{
          bgcolor: 'white',
          borderBottom: '1px solid',
          borderColor: 'divider',
          position: 'sticky',
          top: 0,
          zIndex: 30,
        }}
      >
        <Container maxWidth="lg">
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography
                variant="h5"
                onClick={() => navigate('/')}
                sx={{
                  fontWeight: 800,
                  cursor: 'pointer',
                  background: 'linear-gradient(135deg, #2563EB, #7C3AED)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-0.03em',
                }}
              >
                Vincula
              </Typography>
              <Chip
                icon={<FiberManualRecord sx={{ fontSize: 8 }} />}
                label={wsConnected ? 'Conectado' : 'Desconectado'}
                size="small"
                color={wsConnected ? 'success' : 'default'}
                variant="outlined"
                sx={{ height: 26, '& .MuiChip-icon': { ml: 0.5 } }}
              />
            </Stack>

            <Stack direction="row" alignItems="center" spacing={2}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Avatar sx={{ width: 32, height: 32, bgcolor: '#EEF2FF', color: '#4F46E5', fontSize: '0.8rem' }}>
                  {(user?.first_name?.[0] || 'U').toUpperCase()}
                </Avatar>
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                  <Typography variant="body2" fontWeight={600} color="text.primary" lineHeight={1.2}>
                    {user?.first_name} {user?.last_name}
                  </Typography>
                </Box>
              </Stack>
              <Button
                variant="outlined"
                size="small"
                onClick={logout}
                startIcon={<ExitToApp sx={{ fontSize: 18 }} />}
                sx={{ fontSize: '0.8rem', py: 0.75 }}
              >
                Salir
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Welcome */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h1" sx={{ mb: 0.5, fontSize: { xs: '1.5rem', md: '2rem' } }}>
            Hola, {user?.first_name || 'Usuario'} <WaveIcon width={28} height={28} color="#2563EB" />
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Tu espacio de acompañamiento virtual. Estamos aquí cuando lo necesites.
          </Typography>
        </Box>

        {/* Main Content Tabs */}
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
          <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'white' }}>
            <Tabs
              value={selectedTab}
              onChange={(_, v) => setSelectedTab(v)}
              sx={{ px: 3, minHeight: 48 }}
            >
              <Tab
                label={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <PhoneInTalk sx={{ fontSize: 18 }} />
                    <span>Acompañamiento</span>
                  </Stack>
                }
                sx={{ minHeight: 48 }}
              />
              <Tab
                label={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <History sx={{ fontSize: 18 }} />
                    <span>Mi historial</span>
                  </Stack>
                }
                sx={{ minHeight: 48 }}
              />
              <Tab
                label={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <NotificationsActive sx={{ fontSize: 18 }} />
                    <span>Recordatorios</span>
                    {reminders.filter(r => r.status === 'pending').length > 0 && (
                      <Chip label={reminders.filter(r => r.status === 'pending').length} size="small" color="warning" sx={{ height: 20, fontSize: '0.7rem' }} />
                    )}
                  </Stack>
                }
                sx={{ minHeight: 48 }}
              />
            </Tabs>
          </Box>

          <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#FAFBFC' }}>
            {/* Tab 1: Request Accompaniment */}
            {selectedTab === 0 && (
              <Box>
                {/* Direct AI Call Button */}
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 3, md: 4 },
                    mb: 3,
                    textAlign: 'center',
                    border: '2px solid',
                    borderColor: '#7C3AED',
                    bgcolor: '#FAF5FF',
                    maxWidth: 540,
                    mx: 'auto',
                    borderRadius: 3,
                  }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      bgcolor: '#EDE9FE',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    <SmartToy sx={{ fontSize: 32, color: '#7C3AED' }} />
                  </Box>
                  <Typography variant="h4" sx={{ mb: 1, color: '#5B21B6' }}>
                    Hablar con Vincula
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                    Tu asistente personal te conoce y te recuerda. Hablale cuando quieras.
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={startAICall}
                    disabled={startingAICall}
                    startIcon={startingAICall ? <CircularProgress size={20} color="inherit" /> : <SmartToy />}
                    sx={{
                      px: 5, py: 1.5, fontSize: '1rem', borderRadius: 3,
                      bgcolor: '#7C3AED', '&:hover': { bgcolor: '#6D28D9' },
                    }}
                  >
                    {startingAICall ? 'Conectando...' : 'Iniciar conversación'}
                  </Button>
                </Paper>

                <Divider sx={{ my: 3 }}>
                  <Chip label="o solicitar un acompañante" size="small" />
                </Divider>

                {!isInQueue ? (
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 3, md: 5 },
                      textAlign: 'center',
                      border: '1px dashed',
                      borderColor: '#E2E8F0',
                      bgcolor: 'white',
                      maxWidth: 540,
                      mx: 'auto',
                    }}
                  >
                    <Box
                      sx={{
                        width: 72,
                        height: 72,
                        borderRadius: '50%',
                        bgcolor: '#EFF6FF',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 3,
                      }}
                    >
                      <VideoCall sx={{ fontSize: 36, color: '#2563EB' }} />
                    </Box>
                    <Typography variant="h3" sx={{ mb: 1 }}>
                      ¿Necesitas acompañamiento?
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 380, mx: 'auto' }}>
                      Solicita un acompañante virtual para recibir apoyo emocional o acompañamiento durante procedimientos.
                    </Typography>

                    <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 3 }}>
                      <Chip icon={<Timer sx={{ fontSize: 14 }} />} label="~5-10 min" size="small" variant="outlined" />
                      <Chip icon={<People sx={{ fontSize: 14 }} />} label="Profesionales disponibles" size="small" variant="outlined" color="success" />
                    </Stack>

                    <Button
                      variant="contained"
                      size="large"
                      onClick={joinQueue}
                      disabled={loadingQueue}
                      startIcon={loadingQueue ? <CircularProgress size={20} color="inherit" /> : <VideoCall />}
                      sx={{ px: 5, py: 1.5, fontSize: '1rem', borderRadius: 3 }}
                    >
                      {loadingQueue ? 'Conectando...' : 'Solicitar atención'}
                    </Button>
                  </Paper>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.25 }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        p: { xs: 3, md: 4 },
                        border: '1px solid',
                        borderColor: '#BFDBFE',
                        bgcolor: '#EFF6FF',
                        maxWidth: 540,
                        mx: 'auto',
                      }}
                    >
                      <Stack spacing={3} alignItems="center">
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: '#2563EB',
                              animation: 'pulse 2s infinite',
                              '@keyframes pulse': {
                                '0%': { boxShadow: '0 0 0 0 rgba(37, 99, 235, 0.7)' },
                                '70%': { boxShadow: '0 0 0 10px rgba(37, 99, 235, 0)' },
                                '100%': { boxShadow: '0 0 0 0 rgba(37, 99, 235, 0)' },
                              },
                            }}
                          />
                          <Typography variant="h4" color="#1E40AF">
                            Estás en la cola
                          </Typography>
                        </Stack>

                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Paper elevation={0} sx={{ p: 2.5, textAlign: 'center', bgcolor: 'white', border: '1px solid #DBEAFE' }}>
                              <Typography variant="h2" color="primary" sx={{ lineHeight: 1 }}>
                                {queuePosition || '-'}
                              </Typography>
                              <Typography variant="caption" sx={{ mt: 0.5 }}>Posición</Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={6}>
                            <Paper elevation={0} sx={{ p: 2.5, textAlign: 'center', bgcolor: 'white', border: '1px solid #DBEAFE' }}>
                              <Typography variant="h2" color="#D97706" sx={{ lineHeight: 1 }}>
                                {estimatedWaitTime ? `${Math.round(estimatedWaitTime / 60)}` : '-'}
                              </Typography>
                              <Typography variant="caption" sx={{ mt: 0.5 }}>Min. estimados</Typography>
                            </Paper>
                          </Grid>
                        </Grid>

                        <LinearProgress
                          sx={{
                            width: '100%',
                            height: 6,
                            borderRadius: 3,
                            bgcolor: '#DBEAFE',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 3,
                              bgcolor: '#2563EB',
                            },
                          }}
                        />

                        <Alert severity="info" sx={{ width: '100%', bgcolor: 'white', border: '1px solid #DBEAFE' }}>
                          Mantén esta ventana abierta. Te conectaremos automáticamente cuando sea tu turno.
                        </Alert>

                        <Button
                          variant="outlined"
                          color="error"
                          onClick={leaveQueue}
                          disabled={loadingQueue}
                          startIcon={loadingQueue ? <CircularProgress size={16} /> : <ExitToApp />}
                          sx={{ borderRadius: 3 }}
                        >
                          {loadingQueue ? 'Saliendo...' : 'Salir de la cola'}
                        </Button>
                      </Stack>
                    </Paper>
                  </motion.div>
                )}
              </Box>
            )}

            {/* Tab 2: History */}
            {selectedTab === 1 && (
              <Box>
                {/* Search and filter */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2.5 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Buscar por acompañante o fecha..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ fontSize: 18, color: '#94A3B8' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Stack>

                {loadingHistory ? (
                  <Stack spacing={1.5}>
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} variant="rectangular" height={60} sx={{ borderRadius: 2 }} />
                    ))}
                  </Stack>
                ) : filteredCallHistory.length === 0 ? (
                  <Paper elevation={0} sx={{ textAlign: 'center', py: 8, border: '1px dashed', borderColor: '#E2E8F0', bgcolor: 'white' }}>
                    <History sx={{ fontSize: 48, color: '#CBD5E1', mb: 1.5 }} />
                    <Typography variant="h5" color="text.secondary" fontWeight={500} gutterBottom>
                      {searchTerm ? 'Sin resultados' : 'Sin sesiones previas'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {searchTerm ? 'Intenta con otros términos' : 'Tu primera sesión aparecerá aquí'}
                    </Typography>
                  </Paper>
                ) : (
                  <Stack spacing={1.5}>
                    {filteredCallHistory.map((call, index) => {
                      const isExpanded = expandedCallId === call.id;
                      const callNotes = expandedNotes[call.id] || { loading: false, notes: [] };
                      const videoUrl = expandedVideoUrl[call.id];
                      const summary = getNoteContent(callNotes.notes, 'summary');
                      const transcript = getNoteContent(callNotes.notes, 'transcript');

                      return (
                        <Paper
                          key={call.id}
                          elevation={0}
                          sx={{
                            border: '1px solid',
                            borderColor: isExpanded ? '#93C5FD' : 'divider',
                            borderRadius: 2,
                            overflow: 'hidden',
                            transition: 'border-color 0.2s, box-shadow 0.2s',
                            ...(isExpanded && { boxShadow: '0 4px 20px rgba(37,99,235,0.08)' }),
                            '&:hover': { borderColor: '#93C5FD' },
                          }}
                        >
                          {/* Header — clickable */}
                          <Box
                            onClick={() => toggleCallExpand(call.id)}
                            sx={{
                              p: 2,
                              cursor: 'pointer',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              bgcolor: isExpanded ? '#F8FAFF' : 'white',
                              transition: 'background-color 0.2s',
                              '&:hover': { bgcolor: '#F8FAFF' },
                            }}
                          >
                            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1 }}>
                              <Typography variant="body2" fontWeight={600}>
                                Sesión #{callHistory.length - index}
                              </Typography>
                              <Chip
                                label={getStatusText(call.status || 'completed')}
                                color={getStatusColor(call.status || 'completed')}
                                size="small"
                                sx={{ height: 22, fontSize: '0.7rem' }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {call.companionName || 'IA Vincula'}
                              </Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(call.started_at || call.created_at)}
                              </Typography>
                              {call.duration > 0 && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <AccessTime sx={{ fontSize: 12 }} /> {formatDuration(call.duration)}
                                </Typography>
                              )}
                              {isExpanded ? <ExpandLess sx={{ color: '#64748B' }} /> : <ExpandMore sx={{ color: '#94A3B8' }} />}
                            </Stack>
                          </Box>

                          {/* Expanded detail */}
                          <Collapse in={isExpanded} timeout={300}>
                            <Divider />
                            {callNotes.loading ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress size={28} />
                              </Box>
                            ) : (
                              <Box>
                                {/* Two-column: Video + Summary */}
                                <Stack direction={{ xs: 'column', md: 'row' }} sx={{ minHeight: 200 }}>
                                  {/* Left: Video */}
                                  <Box sx={{
                                    width: { xs: '100%', md: '45%' },
                                    bgcolor: '#0F172A',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minHeight: { xs: 180, md: 220 },
                                  }}>
                                    {videoUrl && videoUrl !== 'none' ? (
                                      <video
                                        src={videoUrl}
                                        controls
                                        style={{ width: '100%', height: '100%', maxHeight: 260, outline: 'none' }}
                                        controlsList="nodownload"
                                      />
                                    ) : videoUrl === 'none' ? (
                                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 4 }}>
                                        <PlayCircleOutline sx={{ fontSize: 36, color: 'rgba(255,255,255,0.3)' }} />
                                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                          No se encontró video
                                        </Typography>
                                      </Box>
                                    ) : (
                                      <Box
                                        onClick={(e) => { e.stopPropagation(); playRecording(call.id); }}
                                        sx={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 4 }}
                                      >
                                        <Box sx={{
                                          width: 52, height: 52, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.15)',
                                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                                          transition: 'all 0.2s', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)', transform: 'scale(1.1)' },
                                        }}>
                                          {recordingLoading === call.id
                                            ? <CircularProgress size={22} sx={{ color: 'white' }} />
                                            : <PlayCircleOutline sx={{ fontSize: 28, color: 'white' }} />}
                                        </Box>
                                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                                          {recordingLoading === call.id ? 'Cargando...' : 'Click para reproducir'}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>

                                  {/* Right: Summary */}
                                  <Box sx={{ flex: 1, p: 2.5, overflow: 'auto', maxHeight: { md: 260 } }}>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                      <SummaryIcon sx={{ fontSize: 18, color: '#2563EB' }} />
                                      <Typography variant="subtitle2" fontWeight={700} color="#2563EB">Resumen</Typography>
                                    </Stack>
                                    {summary ? (
                                      <Typography variant="body2" sx={{ lineHeight: 1.8, whiteSpace: 'pre-wrap', color: '#334155' }}>
                                        {summary}
                                      </Typography>
                                    ) : (
                                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                        No hay resumen disponible para esta llamada.
                                      </Typography>
                                    )}
                                  </Box>
                                </Stack>

                                {/* Bottom: Transcript */}
                                <Divider />
                                <Box sx={{ p: 2.5 }}>
                                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                    <Description sx={{ fontSize: 18, color: '#7C3AED' }} />
                                    <Typography variant="subtitle2" fontWeight={700} color="#7C3AED">Transcripción</Typography>
                                  </Stack>
                                  {transcript ? (
                                    <Paper elevation={0} sx={{
                                      p: 2, maxHeight: 220, overflow: 'auto', bgcolor: '#F8FAFC',
                                      border: '1px solid', borderColor: '#E2E8F0', borderRadius: 1.5,
                                      fontSize: '0.82rem', lineHeight: 1.9, whiteSpace: 'pre-wrap', color: '#475569',
                                    }}>
                                      {transcript}
                                    </Paper>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                      No hay transcripción disponible para esta llamada.
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            )}
                          </Collapse>
                        </Paper>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            )}

            {/* Tab 3: Reminders */}
            {selectedTab === 2 && (
              <Box sx={{ maxWidth: 600, mx: 'auto' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                  <Typography variant="h5">Mis recordatorios</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate('/calendar')}
                    startIcon={<NotificationsActive />}
                  >
                    Ver Calendario
                  </Button>
                </Stack>
                {loadingReminders ? (
                  <Stack spacing={1}>{[1,2,3].map(i => <Skeleton key={i} height={60} />)}</Stack>
                ) : reminders.length === 0 ? (
                  <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px dashed', borderColor: '#E2E8F0', bgcolor: 'white' }}>
                    <NotificationsActive sx={{ fontSize: 48, color: '#CBD5E1', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      No tenés recordatorios. Podés pedirle a Vincula que te recuerde cosas durante una llamada, o tu familia puede creártelos.
                    </Typography>
                  </Paper>
                ) : (
                  <List>
                    {reminders.map(r => {
                      const isMed = r.is_medication;
                      const isConfirmed = !!r.confirmed_at;
                      const needsConfirm = r.status === 'sent' && isMed && !isConfirmed;
                      return (
                        <ListItem
                          key={r.id}
                          secondaryAction={
                            <Stack direction="row" spacing={0.5}>
                              {needsConfirm && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="success"
                                  startIcon={<CheckCircle />}
                                  onClick={async () => {
                                    try {
                                      await apiRequest(`/reminders/${r.id}/confirm`, { method: 'POST' });
                                      fetchReminders();
                                    } catch (e) {}
                                  }}
                                  sx={{ fontSize: '0.75rem', py: 0.5 }}
                                >
                                  Tomé
                                </Button>
                              )}
                              <IconButton edge="end" onClick={() => deleteReminder(r.id)}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Stack>
                          }
                          sx={{
                            bgcolor: needsConfirm ? '#FFF7ED' : isConfirmed ? '#F0FDF4' : 'white',
                            mb: 1, borderRadius: 2,
                            border: '1px solid',
                            borderColor: needsConfirm ? '#FDBA74' : isConfirmed ? '#86EFAC' : 'divider',
                          }}
                        >
                          <ListItemIcon>
                            {isConfirmed ? (
                              <CheckCircle color="success" />
                            ) : (
                              <NotificationsActive color={r.status === 'pending' ? 'warning' : 'action'} />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <span>{r.content}</span>
                                {isMed && <Chip label="Medicación" size="small" color="info" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />}
                              </Stack>
                            }
                            secondary={
                              isConfirmed
                                ? `✅ Confirmado — ${new Date(r.confirmed_at).toLocaleString('es-ES')}`
                                : `${new Date(r.remind_at).toLocaleString('es-ES')} • ${r.status === 'pending' ? 'Pendiente' : r.status === 'sent' ? 'Enviado' : r.status}`
                            }
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </Box>
            )}


          </Box>
        </Paper>

        <Box sx={{ height: { xs: 72, md: 0 } }} />
      </Container>

      <MobileBottomNav />

      {/* Summary Dialog */}
      <Dialog
        open={summaryDialog.open}
        onClose={() => setSummaryDialog({ open: false, callId: null, summary: '', loading: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ArticleOutlined /> Resumen de la llamada
        </DialogTitle>
        <DialogContent dividers>
          {summaryDialog.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
              {summaryDialog.summary}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSummaryDialog({ open: false, callId: null, summary: '', loading: false })}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PatientDashboard;
